import React, { useEffect, useId, useRef, useState } from 'react'
import { pantryColors, pantryRadii } from '@pantry/ui'
import { ChipInput } from './ChipInput'
import { HideIcon } from './CannabinoidTable'
import { MAX_CUSTOM_SPEC_LIST_ITEMS, MAX_CUSTOM_SPEC_LIST_ITEM_LENGTH, MAX_CUSTOM_SPECS, specColors } from '../../constants'
import { emptyCustomSpec } from '../../utils/variationSpecs'
import { CUSTOM_SPEC_TYPES, type CustomSpecification, type CustomSpecType } from '../../../../types/product'

// Typed custom specification cards (ACD-15223 mockup `.mk-cf-card`).
// `value` is edited directly for text / longtext only; number / range / list
// keep their typed fields and `value` is derived at save
// (sanitizeCustomSpecsForSave), so switching the type back and forth never
// loses what was typed.

export const CUSTOM_SPEC_TYPE_META: Record<CustomSpecType, { label: string; icon: string; unit: boolean }> = {
  text: { label: 'Text', icon: 'Aa', unit: false },
  longtext: { label: 'Long text', icon: '¶', unit: false },
  number: { label: 'Number', icon: '#', unit: true },
  range: { label: 'Range', icon: '↔', unit: true },
  list: { label: 'List', icon: '≡', unit: false },
}

export const CUSTOM_SPEC_UNIT_SUGGESTIONS = ['%', 'mg', 'g', 'ml', 'mg/g', 'mg/ml', 'mg/unit', 'pcs']

interface CustomSpecificationsInputProps {
  value: CustomSpecification[]
  onChange: (rows: CustomSpecification[]) => void
  disabled?: boolean
  // Renders the "+ Add specification" button under the cards (default true).
  // SpecFieldsEditor turns it off and adds rows from its own add control.
  showAddButton?: boolean
  addLabel?: string
  // Focuses the name input of this row once (e.g. right after it was added
  // from outside); `onAutoFocusHandled` lets the parent clear it.
  autoFocusIndex?: number | null
  onAutoFocusHandled?: () => void
  // Extra content at the bottom of a card body (e.g. the ◆ per-variant diff line).
  renderBelowCard?: (index: number) => React.ReactNode
}

const fieldStyle = (disabled?: boolean): React.CSSProperties => ({
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  padding: '9px 12px',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.sm,
  fontSize: 13,
  color: pantryColors.gray[800],
  background: disabled ? pantryColors.gray[50] : pantryColors.white,
  fontFamily: 'inherit',
  outline: 'none',
})

const cardStyle: React.CSSProperties = {
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.md,
  background: pantryColors.white,
}

const cardTopStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 8px 8px 10px',
  borderBottom: `1px solid ${pantryColors.gray[100]}`,
  background: specColors.cardHeaderBg,
  borderRadius: `${pantryRadii.md}px ${pantryRadii.md}px 0 0`,
}

const typeIconStyle: React.CSSProperties = {
  width: 26,
  height: 26,
  flexShrink: 0,
  borderRadius: pantryRadii.sm,
  background: pantryColors.indigo[50],
  color: pantryColors.indigo[700],
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
  fontWeight: 700,
}

const nameInputStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  border: '1px solid transparent',
  borderRadius: pantryRadii.sm,
  background: 'transparent',
  padding: '5px 8px',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: 'inherit',
  color: pantryColors.gray[800],
  outline: 'none',
}

const typeSelectStyle = (disabled?: boolean): React.CSSProperties => ({
  height: 28,
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.pill,
  background: pantryColors.white,
  padding: '0 8px',
  fontSize: 12,
  fontFamily: 'inherit',
  color: pantryColors.gray[700],
  cursor: disabled ? 'default' : 'pointer',
  outline: 'none',
})

const unitToggleStyle = (on: boolean, disabled?: boolean): React.CSSProperties => ({
  height: 28,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '0 10px',
  border: `1px solid ${on ? pantryColors.blue[200] : pantryColors.gray[200]}`,
  borderRadius: pantryRadii.pill,
  background: on ? pantryColors.blue[50] : pantryColors.white,
  color: on ? pantryColors.blue[700] : pantryColors.gray[400],
  fontSize: 12,
  fontWeight: on ? 500 : 400,
  fontFamily: 'inherit',
  whiteSpace: 'nowrap',
  cursor: disabled ? 'default' : 'pointer',
})

const removeButtonStyle = (disabled?: boolean): React.CSSProperties => ({
  width: 28,
  height: 28,
  flexShrink: 0,
  border: 'none',
  borderRadius: pantryRadii.sm,
  background: 'none',
  color: pantryColors.gray[400],
  cursor: disabled ? 'default' : 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
})

const inlineStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 }

export const specAddButtonStyle = (disabled?: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  height: 30,
  padding: '0 12px',
  border: `1px solid ${pantryColors.gray[200]}`,
  borderRadius: pantryRadii.sm,
  background: pantryColors.white,
  color: pantryColors.gray[800],
  fontSize: 12,
  fontWeight: 500,
  fontFamily: 'inherit',
  cursor: disabled ? 'default' : 'pointer',
})

export const PlusIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 12, height: 12, flexShrink: 0 }} aria-hidden>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
)

const EyeIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 13, height: 13, flexShrink: 0 }} aria-hidden>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
)

function parseNumber(raw: string): number | null {
  if (raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

interface CustomSpecCardProps {
  row: CustomSpecification
  index: number
  disabled?: boolean
  unitListId: string
  nameRef: (el: HTMLInputElement | null) => void
  onPatch: (patch: Partial<CustomSpecification>) => void
  onRemove: () => void
  below?: React.ReactNode
}

const CustomSpecCard: React.FC<CustomSpecCardProps> = ({
  row,
  index,
  disabled,
  unitListId,
  nameRef,
  onPatch,
  onRemove,
  below,
}) => {
  const type = row.type ?? 'text'
  const meta = CUSTOM_SPEC_TYPE_META[type] ?? CUSTOM_SPEC_TYPE_META.text
  const n = index + 1
  const showUnitInput = meta.unit && row.has_unit === true

  const unitInput = showUnitInput ? (
    <input
      type="text"
      list={unitListId}
      value={row.unit ?? ''}
      disabled={disabled}
      maxLength={20}
      onChange={(e) => onPatch({ unit: e.target.value })}
      placeholder="Unit"
      aria-label={`Specification ${n} unit`}
      style={{ ...fieldStyle(disabled), flex: '0 0 110px' }}
    />
  ) : null

  let body: React.ReactNode
  if (type === 'longtext') {
    body = (
      <textarea
        rows={3}
        value={row.value ?? ''}
        disabled={disabled}
        onChange={(e) => onPatch({ value: e.target.value })}
        placeholder="Enter details…"
        aria-label={`Specification ${n} value`}
        style={{ ...fieldStyle(disabled), resize: 'vertical', minHeight: 72, lineHeight: 1.5, display: 'block' }}
      />
    )
  } else if (type === 'number') {
    body = (
      <div style={inlineStyle}>
        <input
          type="number"
          step="any"
          value={row.number ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ number: parseNumber(e.target.value) })}
          placeholder="Value"
          aria-label={`Specification ${n} value`}
          style={{ ...fieldStyle(disabled), flex: 1 }}
        />
        {unitInput}
      </div>
    )
  } else if (type === 'range') {
    body = (
      <div style={inlineStyle}>
        <input
          type="number"
          step="any"
          value={row.min ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ min: parseNumber(e.target.value) })}
          placeholder="Min"
          aria-label={`Specification ${n} min`}
          style={{ ...fieldStyle(disabled), flex: 1 }}
        />
        <span style={{ fontSize: 12, color: pantryColors.gray[400] }}>to</span>
        <input
          type="number"
          step="any"
          value={row.max ?? ''}
          disabled={disabled}
          onChange={(e) => onPatch({ max: parseNumber(e.target.value) })}
          placeholder="Max"
          aria-label={`Specification ${n} max`}
          style={{ ...fieldStyle(disabled), flex: 1 }}
        />
        {unitInput}
      </div>
    )
  } else if (type === 'list') {
    body = (
      <ChipInput
        value={row.items ?? []}
        onChange={(items) => onPatch({ items })}
        placeholder="Add value…"
        chipColor="gray"
        disabled={disabled}
        maxItems={MAX_CUSTOM_SPEC_LIST_ITEMS}
        maxLength={MAX_CUSTOM_SPEC_LIST_ITEM_LENGTH}
        aria-label={`Specification ${n} values`}
      />
    )
  } else {
    body = (
      <input
        type="text"
        value={row.value ?? ''}
        disabled={disabled}
        onChange={(e) => onPatch({ value: e.target.value })}
        placeholder="Value, e.g. Greenhouse"
        aria-label={`Specification ${n} value`}
        style={fieldStyle(disabled)}
      />
    )
  }

  return (
    <div style={cardStyle} data-custom-spec-card={index}>
      <div style={cardTopStyle}>
        <span style={typeIconStyle} title={meta.label} aria-hidden>
          {meta.icon}
        </span>
        <input
          ref={nameRef}
          type="text"
          value={row.key}
          disabled={disabled}
          onChange={(e) => onPatch({ key: e.target.value })}
          placeholder="Specification name"
          aria-label={`Specification ${n} name`}
          style={nameInputStyle}
        />
        <select
          value={type}
          disabled={disabled}
          onChange={(e) => onPatch({ type: e.target.value as CustomSpecType })}
          title="Field type"
          aria-label={`Specification ${n} type`}
          style={typeSelectStyle(disabled)}
        >
          {CUSTOM_SPEC_TYPES.map((t) => (
            <option key={t} value={t}>
              {CUSTOM_SPEC_TYPE_META[t].label}
            </option>
          ))}
        </select>
        {meta.unit && (
          <button
            type="button"
            aria-pressed={row.has_unit === true}
            disabled={disabled}
            onClick={() => onPatch({ has_unit: !row.has_unit })}
            title={row.has_unit ? 'Unit shown — click to remove' : 'No unit — click to add one'}
            aria-label={`Specification ${n} unit toggle`}
            style={unitToggleStyle(row.has_unit === true, disabled)}
          >
            {row.has_unit ? <EyeIcon /> : <HideIcon size={13} />}
            Unit
          </button>
        )}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          title="Remove specification"
          aria-label={`Remove specification ${n}`}
          style={removeButtonStyle(disabled)}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }} aria-hidden>
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      <div style={{ padding: 10 }}>
        {body}
        {below}
      </div>
    </div>
  )
}

export const CustomSpecificationsInput: React.FC<CustomSpecificationsInputProps> = ({
  value,
  onChange,
  disabled,
  showAddButton = true,
  addLabel = 'Add specification',
  autoFocusIndex = null,
  onAutoFocusHandled,
  renderBelowCard,
}) => {
  const unitListId = `${useId()}-units`
  const nameRefs = useRef<(HTMLInputElement | null)[]>([])
  const [pendingFocus, setPendingFocus] = useState<number | null>(null)
  const focusIndex = autoFocusIndex ?? pendingFocus

  useEffect(() => {
    if (focusIndex == null) return
    const input = nameRefs.current[focusIndex]
    if (!input) return
    input.focus()
    setPendingFocus(null)
    onAutoFocusHandled?.()
  }, [focusIndex, value.length, onAutoFocusHandled])

  const updateRow = (index: number, patch: Partial<CustomSpecification>) => {
    onChange(value.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const removeRow = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  const atLimit = value.length >= MAX_CUSTOM_SPECS

  const addRow = () => {
    if (atLimit) return
    setPendingFocus(value.length)
    onChange([...value, emptyCustomSpec()])
  }

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {value.map((row, index) => (
            <CustomSpecCard
              // Rows have no stable id; index keys keep the inputs controlled.
              key={index}
              row={row}
              index={index}
              disabled={disabled}
              unitListId={unitListId}
              nameRef={(el) => {
                nameRefs.current[index] = el
              }}
              onPatch={(patch) => updateRow(index, patch)}
              onRemove={() => removeRow(index)}
              below={renderBelowCard?.(index)}
            />
          ))}
        </div>
      )}

      <datalist id={unitListId}>
        {CUSTOM_SPEC_UNIT_SUGGESTIONS.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>

      {showAddButton && (
        <button
          type="button"
          onClick={addRow}
          disabled={disabled || atLimit}
          title={atLimit ? `Up to ${MAX_CUSTOM_SPECS} custom specifications` : undefined}
          style={{ ...specAddButtonStyle(disabled || atLimit), marginTop: value.length > 0 ? 10 : 0 }}
        >
          <PlusIcon />
          {addLabel}
        </button>
      )}
    </div>
  )
}
